import { Injectable } from '@angular/core';
import { App, AppState } from '@capacitor/app';
import { AvailableResult, BiometryType, NativeBiometric } from 'capacitor-native-biometric';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from './user/user.service';
import { Dayjs } from 'dayjs';
import * as dayjs from 'dayjs';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { LoggerService } from './logger/logger.service';
import { SignalsService } from './signals.service';
import { RestApiService } from './rest-api/rest-api.service';
import { ApiDevicePlatforms } from './rest-api/rest-api';
import packageInfo from 'package.json';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PhoneNativeService {
  private lastTimeSuccessfullyChecked!: Dayjs | null;
  private lastTimeActive: Dayjs | null = null; // additional check to prevent BI-413
  private biometryChecking = false;
  public biometryLocked = new BehaviorSubject<boolean>(false);

  public getBiometryLocked(): Observable<boolean> {
    return this.biometryLocked.asObservable();
  }

  public constructor(
    private translateService: TranslateService,
    private userService: UserService,
    private loggerService: LoggerService,
    private signalsService: SignalsService,
    private restApiService: RestApiService,
  ) {
    // todo - add some service for window watchers - issues/127
    window.addEventListener('click', this.updateLastActive.bind(this));
    window.addEventListener('scroll', this.updateLastActive.bind(this));
    window.addEventListener('touch', this.updateLastActive.bind(this));
  }

  private updateLastActive(): void {
    this.lastTimeActive = dayjs();
  }

  public initListeners(): void {
    App.addListener('appStateChange', this.appStateChange.bind(this));
    // init biometric call when app is restarted
    this.appStateChange({isActive: true});
  }

  private appStateChange(state: AppState) {
    // hide app preview when inactive, only on iOS
    if(!state.isActive) {
      this.biometryLocked.next(true);
    }

    from(NativeBiometric.isAvailable({ useFallback: true })).subscribe({
      next: (result: AvailableResult) => {
        if(
          this.userService.loggedUser
          && state.isActive
          && (!this.lastTimeSuccessfullyChecked
            || dayjs().diff(this.lastTimeSuccessfullyChecked, 'minutes') >= 2)
          && result.biometryType !== BiometryType.NONE
          && (!this.lastTimeActive
            || dayjs().diff(this.lastTimeActive, 'minutes') >= 2)
          && this.signalsService.translationsLoaded() // init case is handled from app.component
        ) {
          this.biometricAuth();
        } else if(state.isActive && this.signalsService.translationsLoaded()) {
          this.biometryLocked.next(false);
          this.lastTimeSuccessfullyChecked = null;
        }
      },
      error: (err) => {
        // log unexpected biometric error but dont deny user from app access
        this.loggerService.error('Biometric error (3)', err);
        this.biometryLocked.next(false);
      },
    });
  }

  public biometricAuth(): void {
    if(this.biometryChecking) return;
    this.biometryChecking = true;

    this.biometryLocked.next(true);

    from(NativeBiometric.verifyIdentity({
      reason: this.translateService.instant('nativePhoneAuth.reason'),
      title: this.translateService.instant('nativePhoneAuth.reason'),
      useFallback: true,
    })).subscribe({
      next: this.biometricSuccessResult.bind(this),
      error: this.biometricResultError.bind(this), // failed auth = error callback
    });
  }

  private biometricSuccessResult(): void {
    this.biometryChecking = false;
    this.lastTimeActive = dayjs();
    this.lastTimeSuccessfullyChecked = dayjs();
    this.biometryLocked.next(false);
  }

  private biometricResultError(error: unknown): void {
    this.biometryChecking = false;

    try {
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        'message' in error &&
        typeof error['code'] === 'string'
      ) {
        //check capacitor-native-biometric/BiometricAuthError for other codes
        const shouldRecheckCodes = [
          '10', // print mismatch
          '16', // user cancel
        ];
        // do not log to sentry/expected fails
        const dontLogCodes = [
          '3', // no available biometrics
          '13', // not interactive - too many failed attempts or locked device
          '15', // system cancel (ex. app move to background)
        ];

        if(error['code'] && shouldRecheckCodes.includes(error['code'])) {
          this.lastTimeSuccessfullyChecked = null;
          this.biometricAuth();
        } else if(error['code'] && dontLogCodes.includes(error['code'])) {
          this.lastTimeSuccessfullyChecked = null;
        } else {
          this.loggerService.error('Biometric error (1)', error);
          this.biometryLocked.next(false);
        }
      } else {
        this.loggerService.error('Unknown biometric error', error);
        this.biometryLocked.next(false);
      }
    } catch (error) {
      this.loggerService.error('Biometric error (2)', error);
      this.biometryLocked.next(false);
    }
  }

  public isVersionValid(device: ApiDevicePlatforms | null): Observable<boolean> {
    // All error cases determine valid as TRUE not to block the user's version due to server issues etc.

    if (!device) {
      this.loggerService.error('Only android or ios devices are allowed!');
      return of(true)
    }

    let appVersion: string;
    try {
      appVersion = packageInfo.version;
      this.loggerService.logInfo(appVersion);
    } catch (error) {
      this.loggerService.error('Failed to set app version!', error);
      return of(true);
    }

    const regex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
    const validVersionFormat = regex.test(appVersion);

    if (!appVersion || !validVersionFormat) {
      this.loggerService.error('App version set to invalid value: ', appVersion);
      return of(true)
    }
    return this.restApiService.validatePhoneAppVersion(device, appVersion).pipe(
      map(res => res.valid),
      catchError(err => {
        this.loggerService.error('Could not retrieve app version validation from the server, app version: ' + appVersion + ', error: ', err ? err : 'general');
        return of(true);
      })
    );
  }
}
